import { Controller } from "@hotwired/stimulus"

let recaptchaPromise
const RECAPTCHA_TIMEOUT_MS = 10000

function loadRecaptcha(siteKey) {
  if (window.grecaptcha?.execute) return Promise.resolve(window.grecaptcha)

  if (!recaptchaPromise) {
    recaptchaPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
      script.async = true
      script.defer = true
      script.onload = () => resolve(window.grecaptcha)
      script.onerror = () => {
        script.remove()
        recaptchaPromise = undefined
        reject(new Error("Unable to load verification"))
      }
      document.head.appendChild(script)
    })
  }

  return recaptchaPromise
}

function withTimeout(promise, message) {
  let timeoutId

  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), RECAPTCHA_TIMEOUT_MS)
  })

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId))
}

function executeRecaptcha(recaptcha, siteKey, action) {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      try {
        resolve(await recaptcha.execute(siteKey, { action }))
      } catch (error) {
        reject(error)
      }
    }

    if (typeof recaptcha.ready === "function") {
      recaptcha.ready(execute)
    } else {
      execute()
    }
  })
}

export default class extends Controller {
  static targets = ["token", "status", "submit"]
  static values = {
    siteKey: String,
    action: { type: String, default: "customer_signup" }
  }

  connect() {
    this.pendingSubmit = false

    if (!this.siteKeyValue) {
      this.setStatus("Registration is temporarily unavailable. Please try again later.")
      this.setSubmitDisabled(true)
      return
    }

    this.setSubmitDisabled(false)
  }

  async submit(event) {
    if (this.pendingSubmit) return

    event.preventDefault()

    if (!this.siteKeyValue) {
      this.setStatus("Registration is temporarily unavailable. Please try again later.")
      return
    }

    this.pendingSubmit = true
    if (this.hasTokenTarget) this.tokenTarget.value = ""
    this.setSubmitDisabled(true)
    this.setStatus("Verifying...")

    try {
      const recaptcha = await loadRecaptcha(this.siteKeyValue)
      const token = await withTimeout(
        executeRecaptcha(recaptcha, this.siteKeyValue, this.actionValue),
        "Verification timed out"
      )

      this.tokenTarget.value = token
      this.setStatus("Verification complete.")
      this.element.requestSubmit()
    } catch {
      this.pendingSubmit = false
      this.setSubmitDisabled(false)
      this.setStatus("Verification could not load. Please refresh the page and try again.")
    }
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }
}
